<template>
  <div class="relative flex gap-x-2 w-full">
    <swiper
      v-if="activeWorkflowPhases.length"
      :modules="[Controller, Mousewheel]"
      class="w-full"
      :controller="{ control: swiperRef }"
      :slides-per-view="2"
      watch-overflow
      :breakpoints="sliderBreakpoints"
      :mousewheel="{
        enabled: true,
        forceToAxis: true,
      }"
      @init="setupSwiper"
      @slide-change="handleSlideChange"
      @resize="handleSlideChange"
    >
      <swiper-slide
        v-for="(phase, index) in activeWorkflowPhases"
        :key="phase"
        :style="{
          'z-index': 200 - index,
        }"
      >
        <app-phase-tag
          :active-phase="activePhase"
          :client-name="clientName"
          :current-phase="indexOfCurrentPhase || undefined"
          :first="index === 0"
          :index="index"
          :is-managed-by-client="isManagedByClient(index)"
          :is-managed-by-lendflow="isManagedByLendflow(index)"
          :last="isLast(String(index))"
          :phase-name="phase"
          :show-completed-icon="hasCompletedPhase(index)"
          :show-dead-icon="isDeadAtPhase(String(index), phase)"
          data-cy="stage-phase-tag"
          @click="handlePhaseClick(phase, index)"
        />
      </swiper-slide>
    </swiper>

    <button
      v-if="showSwiperPreviousButton"
      class="left-0 absolute w-6 h-6 bg-medium-grey z-200 flex items-center justify-center"
      @click="swiperRef?.slidePrev()"
    >
      <icon-base
        class="rotate-180 relative -top-px"
        :icon="IconChevronRight"
        :width="6"
        :height="10"
      />
    </button>

    <button
      v-if="showSwiperNextButton"
      class="right-8 absolute w-6 h-6 bg-medium-grey z-200 flex items-center justify-center"
      @click="swiperRef?.slideNext()"
    >
      <icon-base :icon="IconChevronRight" :width="6" :height="12" />
    </button>

    <div
      v-if="workflowBuilderName"
      v-tooltip="workflowBuilderName"
      :class="{ 'cursor-pointer': canViewWorkflowTemplate }"
      @click="openWorkflowTemplate"
    >
      <icon-base
        class="rounded-sm bg-medium-grey p-0.5"
        :icon="IconWorkflowLinear"
        height="24"
        width="26"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useStore } from "vuex";

import { storeToRefs } from "pinia";
import { Controller, Mousewheel } from "swiper/modules";
import "swiper/swiper.css";
import { Swiper, SwiperSlide } from "swiper/vue";

import { AnalyticsEvent } from "@/enums/analytics";
import { Ability, PermissionSubject } from "@/enums/auth";
import { ShortcutAction } from "@/enums/keyboardShortcuts";
import { PHASE_ABSTRACT_APPROVED, PHASE_COMPLETE } from "@/helpers/constants";
import { getDealStatusName } from "@/helpers/formatting";
import { usePermissions } from "@/hooks/auth";
import { useDeals } from "@/hooks/deals";
import { useKeyboardShortcuts } from "@/hooks/keyboardShortcuts";
import {
  useActiveStage,
  useActiveWorkflowTemplate,
  useActiveWorkflowTemplateStages,
} from "@/hooks/workflow";
import { ROUTE_DEAL_PROGRESS, ROUTE_WORKFLOW_BUILDER } from "@/router/routes";
import { useAnalyticsStore } from "@/stores/analytics";
import useApplicationsStore from "@/stores/applications";
import useNotesStore from "@/stores/notes";

import IconChevronRight from "@/components/icons/IconChevronRight.vue";
import IconWorkflowLinear from "@/components/icons/workflow/IconWorkflowLinear.vue";
import AppPhaseTag from "@/views/deals/components/AppPhaseTag.vue";

import type { IRootState } from "@/models/state";
import type TSwiper from "swiper";

const route = useRoute();
const router = useRouter();

const { getters, commit, dispatch } = useStore<IRootState>();
const { activeDeal: deal, activeDealPhases } = useDeals();
const {
  activeTemplateStages,
  currentPhaseName,
  activeWorkflowPhases,
  indexOfCurrentPhase,
} = useActiveWorkflowTemplateStages();
const {
  activeStage,
  isUnderwritingStage,
  isApplicationStage,
  isScorecardsStage,
} = useActiveStage();
const { activeTemplate } = useActiveWorkflowTemplate();
const applicationsStore = useApplicationsStore();
const notesStore = useNotesStore();
const { trackEvent } = useAnalyticsStore();
const { shouldFetchBusinessNotes, shouldFetchOffers } =
  storeToRefs(applicationsStore);
const { canPerformActionReactive } = usePermissions();
const { assignShortcutToAction } = useKeyboardShortcuts();

const swiperRef = ref<TSwiper | null>(null);
const showSwiperPreviousButton = ref(false);
const showSwiperNextButton = ref(false);

const activePhase = ref(0);
const noDeadStatus = -1;

const canViewWorkflowTemplate = canPerformActionReactive(
  PermissionSubject.workflowTemplate,
  Ability.view,
  {
    workflowTemplate: deal.value?.workflow_template_id,
  }
);

const isDeadAtApplication = computed(() => {
  const deadStatusNumber: string = getters["options/deadStatus"];
  return (
    deal.value.status === Number(deadStatusNumber) &&
    deal.value?.death?.previous_status === null
  );
});

const dealAllowedStep = computed(() => {
  let statusName = currentPhaseName.value;

  if (statusName === PHASE_COMPLETE) {
    statusName = PHASE_ABSTRACT_APPROVED;
  }

  return activeWorkflowPhases.value
    .map((phase) => phase.toLowerCase())
    .indexOf(statusName.toLowerCase());
});

const workflowBuilderName = computed(() => activeTemplate.value.name);

const clientName = computed(() => deal.value?.client?.name);

const sliderBreakpoints = computed(() => {
  const maxSlides = activeWorkflowPhases.value.length;

  return {
    768: { slidesPerView: Math.min(3, maxSlides) },
    1024: { slidesPerView: Math.min(4, maxSlides) },
    1280: { slidesPerView: Math.min(5, maxSlides) },
    1536: { slidesPerView: Math.min(6, maxSlides) },
    1780: { slidesPerView: Math.min(7, maxSlides) },
  };
});

const isManagedByClient = (index: number) =>
  activeTemplateStages.value[index]?.management?.client;

const isManagedByLendflow = (index: number) =>
  activeTemplateStages.value[index]?.management?.lendflow;

const handlePhaseClick = async (phase: string, index: number) => {
  const phaseIndex = activeWorkflowPhases.value.indexOf(phase);

  if (deal.value.status === 8 && deal.value.status < phaseIndex + 1) {
    return;
  }

  const workflowStage = activeTemplate.value.content?.stages[index];
  commit("workflows/setActiveStage", workflowStage);
  commit("applications/setCurrentPhase", phaseIndex);

  setActivePhaseIndex(index);

  trackEvent(AnalyticsEvent.WflStageViewed, {
    name: workflowStage?.name,
    type: workflowStage?.type,
    id: workflowStage?.id,
    applicationId: deal.value.id,
  });

  if (!workflowStage?.name) {
    return;
  }

  if (route.name === ROUTE_DEAL_PROGRESS) {
    router.replace({
      ...route,
      query: {
        ...route.query,
        stage: String(workflowStage.name),
      },
    });
  } else {
    router.replace({
      name: ROUTE_DEAL_PROGRESS,
      params: {
        id: route.params.id,
      },
      query: {
        ...route.query,
        stage: String(workflowStage.name),
      },
    });
  }

  if (isApplicationStage.value && shouldFetchBusinessNotes.value) {
    applicationsStore.updateFetchServiceStatus(["businessNotes"], false);
    await notesStore.getNotes({
      application_ids: [deal.value.id],
    });
  }

  const isOffersStage = [
    isApplicationStage.value,
    isUnderwritingStage.value,
    isScorecardsStage.value,
  ].every((stage) => !stage); // if all stages are false, then we are in offers stage

  if (isOffersStage && shouldFetchOffers.value) {
    applicationsStore.updateFetchServiceStatus(["offers"], false);
    await dispatch("applications/getOffers");
  }
};

const setActivePhaseIndex = (index: number) => {
  activePhase.value = index;
};

const isLast = (index: string) => {
  return Number(index) === activeWorkflowPhases.value.length - 1;
};

const hasCompletedPhase = (index: number) => {
  //status deal died in
  const deadStatusId = deal.value?.death
    ? deal.value?.death.previous_status
    : noDeadStatus;

  if (deadStatusId !== noDeadStatus) {
    return indexOfCurrentPhase.value >= index;
  }

  const statusName = activeWorkflowPhases.value[deadStatusId] || "";
  const statusIndex = activeDealPhases.value.indexOf(statusName);
  if (statusName) {
    return statusIndex >= index;
  }

  if (dealAllowedStep.value === activeWorkflowPhases.value.length - 1) {
    return true;
  }

  const finishedPhases = activeWorkflowPhases.value.slice(
    0,
    dealAllowedStep.value || 0
  );
  return finishedPhases.includes(activeWorkflowPhases.value[index]);
};

const isDeadAtPhase = (phaseIndex: string, phaseName: string) => {
  if (isDeadAtApplication.value && phaseIndex === "0") {
    return true;
  }

  if (!deal.value?.death?.previous_status) {
    return false;
  }

  if (indexOfCurrentPhase.value === Number(phaseIndex)) {
    return true;
  }

  const { status_description } = getDealStatusName(
    deal.value?.death?.previous_status
  );

  return phaseName.toLowerCase() === status_description.toLowerCase();
};

const openWorkflowTemplate = () => {
  if (!canViewWorkflowTemplate.value) {
    return;
  }

  const url = router.resolve({
    name: ROUTE_WORKFLOW_BUILDER,
    params: {
      id: deal.value.workflow_template_id,
    },
  }).href;
  window.open(url, "_blank");
};

const incrementPhase = (increment: -1 | 1) => {
  const current = activePhase.value;
  if (
    (increment === -1 && current === 0) ||
    (increment === 1 && current === activeWorkflowPhases.value.length - 1)
  ) {
    return;
  }
  const targetIndex = current + increment;
  const targetPhase = activeWorkflowPhases.value[targetIndex];
  handlePhaseClick(targetPhase, targetIndex);
};

const setupSwiper = async (swiper: TSwiper) => {
  swiperRef.value = swiper;

  if (!swiperRef.value) {
    return;
  }

  const stageIndex = activeWorkflowPhases.value.findIndex(
    (name) => name === activeStage.value?.name
  );

  swiperRef.value.slideTo(stageIndex);
  handleSlideChange(swiperRef.value);
};

const handleSlideChange = async (swiper: TSwiper) => {
  if (!swiper.slides) {
    return;
  }

  showSwiperPreviousButton.value = swiper.activeIndex > 0;
  showSwiperNextButton.value =
    swiper.activeIndex <
    swiper.slides.length - (swiper.params.slidesPerView as number);
};

assignShortcutToAction({
  actionName: ShortcutAction.NextDealStage,
  action: () => incrementPhase(1),
});
assignShortcutToAction({
  actionName: ShortcutAction.PreviousDealStage,
  action: () => incrementPhase(-1),
});

watch(
  activeStage,
  (stageValue) => {
    const stageIndex = activeWorkflowPhases.value.findIndex(
      (name) => name === stageValue?.name
    );
    setActivePhaseIndex(stageIndex);

    if (swiperRef.value) {
      swiperRef.value.slideTo(stageIndex);
    }
  },
  { immediate: true }
);

defineExpose({ setActivePhaseIndex });
</script>
